import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Person,
  Email,
  CalendarToday,
  Edit,
  Save,
  Cancel,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import axios from 'axios'

import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    name: '',
    biologicalSex: '',
    dateOfBirth: null as dayjs.Dayjs | null,
    raceEthnicity: '',
    heightCm: '',
    weightKg: '',
    bmi: '',
  })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')

  const calculateBMI = (heightCm: string, weightKg: string): string => {
    const height = parseFloat(heightCm)
    const weight = parseFloat(weightKg)
    
    if (height > 0 && weight > 0) {
      // BMI = weight(kg) / (height(m))^2
      const heightInMeters = height / 100
      const bmi = weight / (heightInMeters * heightInMeters)
      return bmi.toFixed(1)
    }
    return ''
  }

  useEffect(() => {
    if (user) {
      const heightCm = user.profile?.heightCm?.toString() || ''
      const weightKg = user.profile?.weightKg?.toString() || ''
      const calculatedBmi = calculateBMI(heightCm, weightKg)
      
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        name: user.profile?.name || '',
        biologicalSex: user.profile?.biologicalSex || '',
        dateOfBirth: user.profile?.dateOfBirth ? dayjs(user.profile.dateOfBirth) : null,
        raceEthnicity: user.profile?.raceEthnicity || '',
        heightCm: heightCm,
        weightKg: weightKg,
        bmi: calculatedBmi || user.profile?.bmi?.toString() || '',
      })
    }
  }, [user])

  const handleEdit = () => {
    setEditing(true)
    setMessage('')
    setError('')
  }

  const handleCancel = () => {
    setEditing(false)
    setMessage('')
    setError('')
    // Reset form data
    if (user) {
      const heightCm = user.profile?.heightCm?.toString() || ''
      const weightKg = user.profile?.weightKg?.toString() || ''
      const calculatedBmi = calculateBMI(heightCm, weightKg)
      
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        name: user.profile?.name || '',
        biologicalSex: user.profile?.biologicalSex || '',
        dateOfBirth: user.profile?.dateOfBirth ? dayjs(user.profile.dateOfBirth) : null,
        raceEthnicity: user.profile?.raceEthnicity || '',
        heightCm: heightCm,
        weightKg: weightKg,
        bmi: calculatedBmi || user.profile?.bmi?.toString() || '',
      })
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      setError('')
      
      // Update profile with all form data
      await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        profile: {
          name: formData.name,
          biologicalSex: formData.biologicalSex,
          dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.format('YYYY-MM-DD') : undefined,
          raceEthnicity: formData.raceEthnicity,
          heightCm: formData.heightCm ? parseFloat(formData.heightCm) : undefined,
          weightKg: formData.weightKg ? parseFloat(formData.weightKg) : undefined,
          bmi: formData.bmi ? parseFloat(formData.bmi) : undefined,
        }
      })
      
      setMessage('Profile updated successfully!')
      setEditing(false)
    } catch (error) {
      setError('Failed to update profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      }
      
      // Auto-calculate BMI when height or weight changes
      if (field === 'heightCm' || field === 'weightKg') {
        const heightCm = field === 'heightCm' ? value : prev.heightCm
        const weightKg = field === 'weightKg' ? value : prev.weightKg
        updated.bmi = calculateBMI(heightCm, weightKg)
      }
      
      return updated
    })
  }

  const handlePasswordChange = async () => {
    setPasswordError('')

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required')
      return
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm.newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter, one lowercase letter, and one number')
      return
    }

    try {
      setLoading(true)
      await axios.put('/users/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      })
      
      setMessage('Password changed successfully!')
      setPasswordDialogOpen(false)
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    } catch (error: any) {
      setPasswordError(error.response?.data?.error || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <LoadingSpinner message="Loading profile..." />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header with Account Card */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={7}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Profile Settings
            </Typography>

            {message && (
              <Alert severity="success" sx={{ mt: 2, maxWidth: 400 }}>
                {message}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 400 }}>
                {error}
              </Alert>
            )}
          </Grid>

          {/* Profile Overview Card */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent sx={{ py: 2, px: 2.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: 'primary.main',
                        fontSize: '1.25rem',
                      }}
                    >
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Chip
                          label={user.emailVerified ? 'Verified' : 'Unverified'}
                          color={user.emailVerified ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                        <Chip
                          label={user?.isPremium ? 'Premium' : 'Free'}
                          color={user?.isPremium ? 'secondary' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {user.email} • Since {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    startIcon={<Lock />}
                    onClick={() => setPasswordDialogOpen(true)}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Change Password
                  </Button>
                </Box>
                
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      AI API Credits
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box textAlign="right">
                        <Typography variant="h6" color="primary" fontWeight="bold" component="span">
                          {user?.apiCredits?.totalCreditsRemaining || 100}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span" ml={0.5}>
                          Remaining
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">•</Typography>
                      <Box textAlign="right">
                        <Typography variant="body2" fontWeight="medium" component="span">
                          {user?.apiCredits?.creditsUsed || 0} / {(user?.apiCredits?.totalFreeCredits || 100) + (user?.apiCredits?.purchasedCredits || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span" ml={0.5}>
                          Used
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Profile Details and Exported Health Profile */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6" fontWeight="medium">
                    Personal Information
                  </Typography>
                  {!editing ? (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={handleEdit}
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <Box>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Box>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editing ? formData.firstName : user.firstName}
                      onChange={(e) => editing && handleInputChange('firstName', e.target.value)}
                      disabled={!editing}
                      InputProps={{
                        startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editing ? formData.lastName : user.lastName}
                      onChange={(e) => editing && handleInputChange('lastName', e.target.value)}
                      disabled={!editing}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={editing ? formData.email : user.email}
                      onChange={(e) => editing && handleInputChange('email', e.target.value)}
                      disabled={!editing}
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={editing ? formData.name : (user.profile?.name || '')}
                      onChange={(e) => editing && handleInputChange('name', e.target.value)}
                      disabled={!editing}
                      helperText="This name will be used throughout the application"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {editing ? (
                      <TextField
                        select
                        fullWidth
                        label="Biological Sex"
                        value={formData.biologicalSex}
                        onChange={(e) => handleInputChange('biologicalSex', e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        <option value="">Select...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        label="Biological Sex"
                        value={user.profile?.biologicalSex || 'Not specified'}
                        disabled
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date of Birth"
                      value={editing ? formData.dateOfBirth : (user.profile?.dateOfBirth ? dayjs(user.profile.dateOfBirth) : null)}
                      onChange={(value) => editing && handleInputChange('dateOfBirth', value)}
                      disabled={!editing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            startAdornment: <CalendarToday sx={{ mr: 1, color: 'action.active' }} />,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Race/Ethnicity"
                      value={editing ? formData.raceEthnicity : (user.profile?.raceEthnicity || '')}
                      onChange={(e) => editing && handleInputChange('raceEthnicity', e.target.value)}
                      disabled={!editing}
                      placeholder="e.g., Caucasian, African American, Hispanic, Asian, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Height (cm)"
                      type="number"
                      value={editing ? formData.heightCm : (user.profile?.heightCm || '')}
                      onChange={(e) => editing && handleInputChange('heightCm', e.target.value)}
                      disabled={!editing}
                      placeholder="170"
                      helperText="Enter height in centimeters"
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Weight (kg)"
                      type="number"
                      value={editing ? formData.weightKg : (user.profile?.weightKg || '')}
                      onChange={(e) => editing && handleInputChange('weightKg', e.target.value)}
                      disabled={!editing}
                      placeholder="70"
                      helperText="Enter weight in kilograms"
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="BMI"
                      value={editing ? formData.bmi : (user.profile?.bmi || '')}
                      disabled
                      placeholder="Auto-calculated"
                      helperText="Calculated from height & weight"
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Health Profile Imported from iOS App */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="medium" gutterBottom sx={{ mb: 2 }}>
                  Health Profile Imported from iOS App
                </Typography>
                
                {user?.exportedHealthProfile ? (
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 600,
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {user.exportedHealthProfile.profile}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No health profile has been exported from the iOS app yet.
                    </Typography>
                  </Box>
                )}
                
                {user?.exportedHealthProfile && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Exported on: {new Date(user.exportedHealthProfile.exportedAt).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Source: {user.exportedHealthProfile.source}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Disclaimers */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {/* Medical Disclaimer */}
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'warning.lighter', borderLeft: 4, borderColor: 'warning.main', height: '100%' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.dark">
                    Medical Disclaimer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This application is not intended to replace professional medical advice, diagnosis or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition or eligibility to participate in a clinical trial. Never disregard or delay professional advice because of something you have read through this app.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Legal Disclaimer */}
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main', height: '100%' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="info.dark">
                    Legal Disclaimer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The information provided in this application is provided "as is" without warranty of any kind. The developers and contributors are not liable for any damages arising from the use of this application. Users are responsible for the accuracy and completeness of health data they input. Users are reminded to not share private health information such as real name, phone number, or other sensitive information in this app.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Privacy Policy */}
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'success.lighter', borderLeft: 4, borderColor: 'success.main', height: '100%' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="success.dark">
                    Privacy Policy
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your private health data is stored securely and is not transmitted to external servers unless you explicitly choose to share it. We respect your privacy and are committed to protecting your personal health information. No personal data is collected for marketing or advertising purposes.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                margin="normal"
                helperText="Must be at least 8 characters with uppercase, lowercase, and number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setPasswordDialogOpen(false)
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
              setPasswordError('')
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePasswordChange}
              disabled={loading}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Privacy Notice */}
        <Card sx={{ mt: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              Privacy & Data Protection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your health data is encrypted and stored securely. We follow HIPAA-compliant practices
              to protect your personal information. Date of birth is stored with month/year precision
              only for privacy protection.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  )
}

export default Profile
